import { useMemo } from "react";
import type { ZonedTimeString } from "@poi-planner/shared";
import type { EventTimingProposal } from "../store/uiStore";
import {
  minutesBetween,
  parseZoned,
  wallTimeCandidates,
  zonedDate,
  zonedFromWallTime,
  zonedTimeOfDay,
} from "../utils/zonedTime";
import { FormField, FormInput, FormSelect } from "./FormFields";
import { TripTimezoneSelect } from "./TripTimezoneSelect";
import { timezoneCity } from "../utils/timezoneDisplay";

export interface EventTimeFieldsValue {
  date: string;
  time: string;
  zone: string;
  offset: string | null;
}

export function eventTimeFieldsFromZoned(
  value: ZonedTimeString,
): EventTimeFieldsValue {
  const zoned = parseZoned(value);
  return {
    date: zonedDate(value),
    time: zonedTimeOfDay(value),
    zone: zoned.timeZoneId,
    offset: zoned.offset,
  };
}

export function zonedFromEventTimeFields(
  fields: EventTimeFieldsValue,
): ZonedTimeString | null {
  if (!fields.date || !fields.time || !fields.zone) return null;
  return zonedFromWallTime(
    fields.date,
    fields.time,
    fields.zone,
    fields.offset,
  );
}

export function eventTimeFieldsError(
  fields: EventTimeFieldsValue,
): string | null {
  if (!fields.date || !fields.time || !fields.zone) return null;
  return wallTimeCandidates(fields.date, fields.time, fields.zone).length === 0
    ? `${fields.time} does not exist in ${fields.zone} on ${fields.date} because the clock moves forward.`
    : null;
}

function formatOffset(offset: string): string {
  return `UTC${offset}`;
}

function EndpointFields({
  label,
  value,
  onChange,
  disabled,
  sameAsZone,
}: {
  label: "Start" | "End";
  value: EventTimeFieldsValue;
  onChange: (value: EventTimeFieldsValue) => void;
  disabled: boolean;
  sameAsZone?: string;
}) {
  const candidates = useMemo(
    () =>
      value.date && value.time && value.zone
        ? wallTimeCandidates(value.date, value.time, value.zone)
        : [],
    [value.date, value.time, value.zone],
  );
  const error = eventTimeFieldsError(value);
  const selectedOffset =
    candidates.find((candidate) => candidate.offset === value.offset)?.offset ??
    candidates[0]?.offset ??
    "";

  const changeWallField = (
    update: Partial<Pick<EventTimeFieldsValue, "date" | "time" | "zone">>,
  ) => onChange({ ...value, ...update, offset: null });

  return (
    <div className="rounded-xl border border-border bg-surface p-3.5">
      <div className="mb-3 flex items-center gap-2">
        <span className="size-2 rounded-full bg-info" aria-hidden />
        <h5 className="m-0 text-xs font-extrabold uppercase tracking-wider text-secondary">
          {label}
        </h5>
      </div>
      <div className="grid grid-cols-1 gap-3">
        <FormField label={`${label} date`}>
          <FormInput
            value={value.date}
            onChange={(date) => changeWallField({ date })}
            type="date"
            disabled={disabled}
            data-testid={`${label.toLowerCase()}-date-input`}
          />
        </FormField>
        <FormField label={`${label} time`}>
          <FormInput
            value={value.time}
            onChange={(time) => changeWallField({ time })}
            type="time"
            disabled={disabled}
            data-testid={`${label.toLowerCase()}-time-input`}
          />
        </FormField>
        <FormField label={`${label} timezone`}>
          <TripTimezoneSelect
            value={value.zone}
            date={value.date}
            onChange={(zone) => changeWallField({ zone })}
            disabled={disabled}
            sameAsZone={sameAsZone}
            data-testid={`${label.toLowerCase()}-zone-select`}
          />
        </FormField>
        {candidates.length > 1 && (
          <div>
            <FormField label={`${label} UTC offset`}>
              <FormSelect
                value={selectedOffset}
                onChange={(offset) => onChange({ ...value, offset })}
                options={candidates.map((candidate) => ({
                  value: candidate.offset,
                  label: `${candidate.occurrence === "earlier" ? "Earlier" : "Later"} occurrence — ${formatOffset(candidate.offset)}`,
                }))}
                disabled={disabled}
                data-testid={`${label.toLowerCase()}-offset-select`}
              />
            </FormField>
          </div>
        )}
      </div>
      {error && (
        <p
          role="alert"
          className="mb-0 mt-2 rounded-lg bg-error-bg px-3 py-2 text-xs font-semibold text-error"
        >
          {error}
        </p>
      )}
    </div>
  );
}

export function EventTimingFields({
  start,
  end,
  onStartChange,
  onEndChange,
  disabled = false,
}: {
  start: EventTimeFieldsValue;
  end: EventTimeFieldsValue;
  onStartChange: (value: EventTimeFieldsValue) => void;
  onEndChange: (value: EventTimeFieldsValue) => void;
  disabled?: boolean;
}) {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <EndpointFields
        label="Start"
        value={start}
        onChange={onStartChange}
        disabled={disabled}
      />
      <EndpointFields
        label="End"
        value={end}
        onChange={onEndChange}
        disabled={disabled}
        sameAsZone={start.zone}
      />
    </div>
  );
}

export function TimezoneChangeNotice({
  proposal,
}: {
  proposal: EventTimingProposal;
}) {
  const duration = Math.round(
    minutesBetween(proposal.startTime, proposal.endTime),
  );
  return (
    <div
      role="status"
      className="flex items-start gap-3 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-blue-950"
      data-testid="timezone-change-notice"
    >
      <span
        className="material-symbols-outlined mt-px text-lg text-info"
        aria-hidden
      >
        travel_explore
      </span>
      <div className="min-w-0">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <strong className="text-sm">Timezone changed</strong>
          <span className="text-xs font-bold text-blue-700">
            {timezoneCity(proposal.originalDisplayZone)} →{" "}
            {timezoneCity(proposal.targetDisplayZone)}
            {` · ${duration} min`}
          </span>
        </div>
        <p className="mb-0 mt-1 text-xs font-medium text-blue-800">
          Review the converted wall times. Nothing changes until you save.
        </p>
      </div>
    </div>
  );
}
